import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, Mail, ArrowLeft, Loader2 } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/request-otp", { email });
      if (data.is_new_user) { navigate("/register", { state: { email } }); return; }
      setStep("otp");
      setCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) { setError(formatError(err)); }
    finally { setLoading(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (val && idx === 5 && next.every(d => d)) verifyOTP(next.join(""));
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
      setTimeout(() => verifyOTP(pasted), 100);
      e.preventDefault();
    }
  };

  const verifyOTP = async (code) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/verify-otp", { email, code });
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatError(err));
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/api/auth/request-otp", { email });
      setCooldown(60);
    } catch (err) { setError(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 24px 40px", position: "relative", overflow: "hidden" }}>
      <div className="bg-blob" style={{ width: 500, height: 500, background: "rgba(0,92,238,0.04)", top: "5%", left: "-10%" }} />
      <div className="bg-blob" style={{ width: 400, height: 400, background: "rgba(138,79,255,0.03)", bottom: "10%", right: "-5%", animationDelay: "3s" }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 2 }}>
        <div className="glass-card" style={{ padding: "2.4rem" }}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: "linear-gradient(135deg, #005CEE, #8A4FFF)",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem",
            }}>
              <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "0.85rem", color: "#fff" }}>T</span>
            </div>
            <h1 data-testid="login-heading" style={{
              fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.4rem",
              letterSpacing: "-0.03em", color: "var(--text-main)", marginBottom: "0.3rem",
            }}>
              {step === "email" ? "Welcome back" : "Enter verification code"}
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", fontFamily: "'Satoshi', sans-serif" }}>
              {step === "email" ? "Sign in to your TIE account" : (<>Code sent to <strong style={{ color: "var(--text-main)" }}>{email}</strong></>)}
            </p>
          </div>

          {error && (
            <div data-testid="login-error" style={{
              display: "flex", gap: 8, alignItems: "flex-start",
              background: "var(--pink-subtle)", border: "1px solid rgba(255,51,102,0.15)",
              borderRadius: 10, padding: "0.65rem 0.9rem", marginBottom: "1.2rem",
            }}>
              <AlertCircle size={14} color="var(--pink)" strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: "0.8rem", color: "var(--pink)", fontFamily: "'Satoshi', sans-serif" }}>{error}</span>
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === "email" ? (
              <motion.form key="email-step" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} onSubmit={requestOTP}>
                <div style={{ marginBottom: "1.5rem" }}>
                  <label className="form-label" htmlFor="login-email">Email</label>
                  <div style={{ position: "relative" }}>
                    <Mail size={15} color="var(--text-muted)" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                    <input id="login-email" data-testid="login-email-input" type="email" className="form-input"
                      value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com" required autoFocus style={{ paddingLeft: 36 }} />
                  </div>
                </div>
                <button type="submit" data-testid="login-submit-btn" className="btn-primary" disabled={loading}
                  style={{ width: "100%", padding: "0.7rem", fontSize: "0.88rem", borderRadius: 12, opacity: loading ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  {loading ? <><Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} /> Sending code...</> : "Send verification code"}
                </button>
              </motion.form>
            ) : (
              <motion.div key="otp-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: "1.5rem" }} onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input key={i} ref={el => (otpRefs.current[i] = el)} data-testid={`otp-input-${i}`}
                      type="text" inputMode="numeric" maxLength={1}
                      value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)}
                      style={{
                        width: 46, height: 54, textAlign: "center", fontSize: "1.3rem", fontWeight: 700,
                        fontFamily: "'Manrope', sans-serif", borderRadius: 12,
                        border: `1.5px solid ${digit ? "var(--primary)" : "var(--bg-neutral)"}`,
                        background: digit ? "var(--accent-subtle)" : "var(--bg-soft)",
                        color: "var(--text-main)", outline: "none", transition: "all 200ms",
                      }}
                      onFocus={e => { e.target.style.borderColor = "var(--primary)"; e.target.style.boxShadow = "0 0 0 3px rgba(0,92,238,0.1)"; }}
                      onBlur={e => { e.target.style.borderColor = digit ? "var(--primary)" : "var(--bg-neutral)"; e.target.style.boxShadow = "none"; }}
                    />
                  ))}
                </div>
                <button data-testid="verify-otp-btn" className="btn-primary" disabled={loading || otp.some(d => !d)}
                  onClick={() => verifyOTP(otp.join(""))}
                  style={{ width: "100%", padding: "0.7rem", fontSize: "0.88rem", borderRadius: 12, opacity: loading || otp.some(d => !d) ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: "1rem" }}>
                  {loading ? <><Loader2 size={15} style={{ animation: "spin 1s linear infinite" }} /> Verifying...</> : "Verify & sign in"}
                </button>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button data-testid="login-back-btn" onClick={() => { setStep("email"); setOtp(["", "", "", "", "", ""]); setError(""); }}
                    style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>
                    <ArrowLeft size={12} /> Change email
                  </button>
                  <button data-testid="resend-otp-btn" onClick={resend} disabled={cooldown > 0}
                    style={{ fontSize: "0.78rem", color: cooldown > 0 ? "var(--text-muted)" : "var(--primary)", fontWeight: 600, background: "none", border: "none", cursor: cooldown > 0 ? "default" : "pointer", fontFamily: "'Manrope', sans-serif" }}>
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ textAlign: "center", marginTop: "1.6rem" }}>
            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>Don't have an account? </span>
            <Link to="/register" style={{ fontSize: "0.8rem", color: "var(--primary)", fontWeight: 600, fontFamily: "'Manrope', sans-serif", textDecoration: "none" }}>Create one</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
